// components/MergeReviewPanel.jsx
// Created: 2026-07-26
// Project: Giving Words to Voices
// Version: 1.0.0
//
// Review what the AI condensed. An AI review folds many timeline events into one
// and rewrites the prose, which is a lot of trust to place in a model when the
// subject is testimony. This screen puts the originals it replaced on the left,
// the condensed event on the right, and lets a person rewrite it in their own
// words before marking it reviewed.
//
// Everything on the right is editable, locators included, so a reviewer can fix
// a span the merge got wrong. The originals are read-only — they are the record.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ArrowLeft, Check, ChevronRight, Clock, GitMerge, Loader, MapPin, Quote, RotateCcw, Save, UserCheck } from 'lucide-react';
import { Icon } from './Icon';
import Button from './ui/Button';
import Input from './ui/Input';

const FIELDS = [
  { key: 'title', label: 'Title', rows: 1 },
  { key: 'description', label: 'Description', rows: 6 },
  { key: 'source_quote', label: 'Source quote', rows: 5, mono: false },
  { key: 'source_locator', label: 'VTT locators', rows: 2, mono: true },
];

export default function MergeReviewPanel({
  theme, isDarkMode, root, subjectName, onBack,
  BACKEND_URL, pushToast = () => {}, axiosAuth,
}) {
  const api = axiosAuth || axios;
  const B = axiosAuth ? '' : BACKEND_URL;

  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!root) { setLoading(false); return; }
    setLoading(true); setFailed(false);
    try {
      const res = await api.get(`${B}/subject/timeline/merge-review?root=${encodeURIComponent(root)}`);
      setItems(res.data?.items || []);
      setMeta(res.data || null);
      setActiveId(prev => prev ?? (res.data?.items?.[0]?.event?.id ?? null));
    } catch (err) {
      setFailed(true);
      pushToast('merge-review', 'error', `Could not load merges: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [api, B, root, pushToast]);

  useEffect(() => { load(); }, [load]);

  const active = useMemo(
    () => items.find(i => i.event.id === activeId) || null,
    [items, activeId],
  );

  // Reset the draft whenever a different merge is opened.
  useEffect(() => {
    setDraft(active ? { ...active.event } : null);
  }, [active]);

  const dirty = useMemo(() => {
    if (!draft || !active) return false;
    return FIELDS.some(f => (draft[f.key] || '') !== (active.event[f.key] || ''));
  }, [draft, active]);

  const save = useCallback(async (alsoMarkReviewed) => {
    if (!draft) return;
    setSaving(true);
    try {
      await api.put(`${B}/subject/timeline/${draft.id}?root=${encodeURIComponent(root)}`, {
        year: draft.year,
        title: draft.title,
        description: draft.description,
        category: draft.category,
        location: draft.location,
        source_quote: draft.source_quote,
        source_locator: draft.source_locator,
        verified: alsoMarkReviewed ? true : !!draft.verified,
      });
      pushToast('merge-review-save', 'success',
        alsoMarkReviewed ? 'Saved and marked reviewed' : 'Changes saved');
      await load();
    } catch (err) {
      pushToast('merge-review-save', 'error', `Save failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  }, [api, B, draft, root, pushToast, load]);

  const goNextUnreviewed = useCallback(() => {
    const idx = items.findIndex(i => i.event.id === activeId);
    const after = items.slice(idx + 1).find(i => !i.event.verified);
    const any = after || items.find(i => !i.event.verified && i.event.id !== activeId);
    if (any) setActiveId(any.event.id);
  }, [items, activeId]);

  const reviewed = items.filter(i => i.event.verified).length;
  const pct = items.length ? Math.round((reviewed / items.length) * 100) : 0;

  const label = { fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: theme.textSub, marginBottom: '4px' };
  const box = { background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '10px', padding: '10px 12px' };

  const field = (f) => (
    <div key={f.key} style={{ marginBottom: '12px' }}>
      <div style={label}>{f.label}</div>
      {f.rows === 1 ? (
        <Input theme={theme} fullWidth value={draft?.[f.key] ?? ''}
          onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))} />
      ) : (
        <textarea
          value={draft?.[f.key] ?? ''}
          onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))}
          rows={f.rows}
          style={{
            width: '100%', boxSizing: 'border-box', padding: '8px 10px',
            background: theme.inputBg || theme.card, color: theme.text,
            border: `1px solid ${theme.border}`, borderRadius: '8px',
            fontSize: f.mono ? '0.7rem' : '0.8rem', lineHeight: 1.5, resize: 'vertical',
            fontFamily: f.mono ? "'SF Mono', Menlo, Consolas, monospace" : 'inherit',
          }}
        />
      )}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderBottom: `1px solid ${theme.border}`, flexShrink: 0, flexWrap: 'wrap' }}>
        <Button theme={theme} variant="outline" size="sm" icon={ArrowLeft} onClick={onBack}>Back</Button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon icon={GitMerge} size={16} color={theme.glossaryAccent} />
          <span style={{ fontWeight: 700, fontSize: '0.9rem', color: theme.text }}>Review AI Merges</span>
          {subjectName && <span style={{ fontSize: '0.75rem', color: theme.textSub }}>{subjectName}</span>}
        </div>
        <div style={{ flex: 1 }} />
        {items.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '120px', height: '6px', borderRadius: '999px', background: theme.border, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: '#34c759', transition: 'width 0.2s' }} />
            </div>
            <span style={{ fontSize: '0.7rem', fontWeight: 700, color: theme.textSub }}>
              {reviewed} / {items.length} reviewed
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', color: theme.textSub }}>
          <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ fontSize: '0.82rem' }}>Loading merges…</span>
        </div>
      ) : failed ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textSub, fontSize: '0.85rem' }}>
          Could not load the merge review.
        </div>
      ) : items.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', textAlign: 'center', padding: '40px' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '999px', background: theme.card, border: `1px solid ${theme.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon icon={GitMerge} size={22} color={theme.textSub} />
          </div>
          <div style={{ fontSize: '1rem', fontWeight: 700, color: theme.text }}>Nothing to review</div>
          <div style={{ fontSize: '0.82rem', color: theme.textSub, maxWidth: '420px', lineHeight: 1.55 }}>
            No timeline events on this subject were condensed from several originals.
            {meta && !meta.snapshot_available && ' There is also no pre-review snapshot stored for this project.'}
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          {/* Merge list */}
          <div style={{ width: '240px', flexShrink: 0, borderRight: `1px solid ${theme.border}`, overflowY: 'auto', padding: '8px' }}>
            {items.map(it => {
              const isActive = it.event.id === activeId;
              return (
                <button key={it.event.id} onClick={() => setActiveId(it.event.id)}
                  style={{
                    width: '100%', textAlign: 'left', marginBottom: '4px', padding: '8px 9px',
                    background: isActive ? `${theme.glossaryAccent}18` : 'none',
                    border: `1px solid ${isActive ? theme.glossaryAccent + '55' : 'transparent'}`,
                    borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '7px',
                  }}>
                  <Icon icon={it.event.verified ? UserCheck : GitMerge} size={12}
                    color={it.event.verified ? '#34c759' : theme.textSub} style={{ marginTop: '2px', flexShrink: 0 }} />
                  <span style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: theme.text, lineHeight: 1.3 }}>{it.event.title}</div>
                    <div style={{ fontSize: '0.6rem', color: theme.textSub, marginTop: '2px' }}>
                      {it.event.year} · {it.origin_count} originals
                    </div>
                  </span>
                </button>
              );
            })}
          </div>

          {/* Comparison */}
          {active && (
            <div style={{ flex: 1, minWidth: 0, display: 'flex', overflow: 'hidden' }}>
              {/* Originals */}
              <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: '14px', borderRight: `1px solid ${theme.border}` }}>
                <div style={{ ...label, marginBottom: '10px' }}>
                  {active.origin_count} originals replaced — read only
                </div>
                {active.origins.map((o, i) => (
                  <div key={o.id ?? i} style={{ ...box, marginBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px' }}>
                      <span style={{ fontSize: '0.62rem', fontWeight: 800, color: theme.glossaryAccent, fontFamily: 'monospace' }}>{o.year}</span>
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: theme.text }}>{o.title}</span>
                    </div>
                    {o.description && (
                      <div style={{ fontSize: '0.72rem', color: theme.textSub, lineHeight: 1.5, marginBottom: '5px' }}>{o.description}</div>
                    )}
                    {o.source_quote && (
                      <div style={{ display: 'flex', gap: '5px', marginBottom: '5px' }}>
                        <Icon icon={Quote} size={11} color={theme.textSub} style={{ flexShrink: 0, marginTop: '2px' }} />
                        <div style={{ fontSize: '0.71rem', fontStyle: 'italic', color: theme.text, lineHeight: 1.45 }}>{o.source_quote}</div>
                      </div>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '0.6rem', color: theme.textSub, fontFamily: 'monospace' }}>
                      {o.location && <span><Icon icon={MapPin} size={9} /> {o.location}</span>}
                      {o.source_locator && <span><Icon icon={Clock} size={9} /> {o.source_locator}</span>}
                    </div>
                  </div>
                ))}
              </div>

              {/* Condensed — editable */}
              <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                  <div style={{ ...label, marginBottom: 0 }}>Condensed — rewrite in your own words</div>
                  {/* Origin and review are separate facts, both worth showing. */}
                  <span style={{ fontSize: '0.55rem', fontWeight: 800, color: theme.textSub, background: `${theme.border}44`, border: `1px solid ${theme.border}`, borderRadius: '999px', padding: '1px 7px' }}>
                    {active.event.source === 'manual' ? 'MANUAL' : 'AI'}
                  </span>
                  {active.event.reviewed_by && (
                    <span title={`Reviewed by ${active.event.reviewed_by}${active.event.reviewed_at ? ` · ${String(active.event.reviewed_at).slice(0, 10)}` : ''}`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '0.58rem', fontWeight: 800, color: '#34c759', background: '#34c75918', border: '1px solid #34c75944', borderRadius: '999px', padding: '1px 7px' }}>
                      <Icon icon={UserCheck} size={9} /> HUMAN REVIEWED
                    </span>
                  )}
                </div>

                {FIELDS.map(field)}

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '14px', paddingTop: '12px', borderTop: `1px solid ${theme.border}` }}>
                  <Button theme={theme} variant="success" icon={Check} disabled={saving}
                    onClick={() => save(true)}>
                    {saving ? 'Saving…' : 'Save & mark reviewed'}
                  </Button>
                  <Button theme={theme} variant="outline" icon={Save} disabled={saving || !dirty}
                    onClick={() => save(false)}>
                    Save only
                  </Button>
                  <Button theme={theme} variant="ghost" icon={RotateCcw} disabled={!dirty}
                    onClick={() => setDraft({ ...active.event })}>
                    Revert edits
                  </Button>
                  <div style={{ flex: 1 }} />
                  <Button theme={theme} variant="outline" icon={ChevronRight}
                    onClick={goNextUnreviewed}>
                    Next unreviewed
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
